import React, { useState } from "react";
import Markdown from "markdown-to-jsx";
import {
  CitationLink,
  CitationTooltip,
  deepSearchCustomLink,
  SourcesSection,
} from "../../../utils/deep-search-helper";
import PropTypes from "prop-types";

// Markdown component overrides with more specific names
const DeepSearchH1 = ({ children }) => (
  <h1 className="!text-lg !font-semibold my-2">{children}</h1>
);

const DeepSearchH2 = ({ children }) => {
  const emojiMatch = children
    ?.toString()
    .match(
      /^([\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}])/u
    );
  if (emojiMatch) {
    return (
      <h2 className="!text-xl !font-bold my-3 flex items-center gap-2">
        <span className="text-2xl">{emojiMatch[1]}</span>
        <span>
          {children.toString().replace(emojiMatch[1], "").trim()}
        </span>
      </h2>
    );
  }
  return (
    <h2 className="!text-base !font-medium my-2">{children}</h2>
  );
};

const DeepSearchH3 = ({ children }) => (
  <h3 className="text-sm my-2 !font-bold my-3 !text-[19px]">
    {children}
  </h3>
);

const DeepSearchUl = ({ children }) => (
  <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>
);

const DeepSearchLi = ({ children }) => (
  <li className="!text-sm">{children}</li>
);

const DeepSearchHr = () => <hr className="my-3 border-gray-200" />;

const DeepSearchP = ({ children }) => {
  if (
    typeof children === "string" &&
    (children.startsWith("Address:") || children.startsWith("Phone:"))
  ) {
    return <p className="!text-sm my-1">{children}</p>;
  }
  return <p className="!text-sm my-2">{children}</p>;
};

const DeepSearchTable = ({ children }) => (
  <div className="overflow-x-auto my-4">
    <table className="min-w-full border-collapse border border-gray-300">
      {children}
    </table>
  </div>
);

const DeepSearchThead = ({ children }) => (
  <thead className="bg-gray-50">{children}</thead>
);

const DeepSearchTh = ({ children }) => (
  <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-sm">
    {children}
  </th>
);

const DeepSearchTd = ({ children }) => (
  <td className="border border-gray-300 px-4 py-2 text-sm">
    {children}
  </td>
);

const DeepSearchBlockquote = ({ children }) => (
  <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 my-3 bg-blue-50 py-2">
    {children}
  </blockquote>
);

const DeepSearchCode = ({ children }) => (
  <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
    {children}
  </code>
);

const DeepSearchPre = ({ children }) => (
  <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm my-3">
    {children}
  </pre>
);

function DeepSearchBotMarkdown({
  content,
  annotations = [],
  visitedURLs = [],
}) {
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [showCitationModal, setShowCitationModal] = useState(false);

  const handleCitationClick = (citationData) => {
    const citationIndex = parseInt(citationData.citationId.split("-")[1]) - 1;
    const annotation = annotations[citationIndex];

    if (annotation && annotation.type === "url_citation") {
      setSelectedCitation({
        title: annotation.url_citation.title,
        url: annotation.url_citation.url,
        date: annotation.url_citation.dateTime,
        exactQuote: annotation.url_citation.exactQuote,
      });
      setShowCitationModal(true);
    }
  };

  const processContentWithCitations = (text) => {
    if (!annotations || annotations.length === 0) return text;

    let processedText = text;

    annotations.forEach((annotation, index) => {
      if (annotation.type === "url_citation") {
        const footnotePattern = new RegExp(`\\[\\^${index + 1}\\]`, "g");
        const replacement = `<citation-link data-citation-index="${index}">[${
          index + 1
        }]</citation-link>`;
        processedText = processedText.replace(footnotePattern, replacement);
      }
    });

    return processedText;
  };

  const CitationMarkdownComponent = ({
    children,
    "data-citation-index": citationIndex,
  }) => {
    const index = parseInt(citationIndex);
    const annotation = annotations[index];

    if (!annotation || annotation.type !== "url_citation") {
      return <span>{children}</span>;
    }

    return (
      <CitationLink
        citationId={`citation-${index + 1}`}
        url={annotation.url_citation.url}
        title={annotation.url_citation.title}
        date={annotation.url_citation.dateTime}
        onClick={handleCitationClick}
      >
        {children}
      </CitationLink>
    );
  };

  CitationMarkdownComponent.propTypes = {
    children: PropTypes.node.isRequired,
    "data-citation-index": PropTypes.string.isRequired,
  };

  const processedContent = processContentWithCitations(content);

  return (
    <div className="bot-markdown-container">
      <Markdown
        options={{
          overrides: {
            "citation-link": { component: CitationMarkdownComponent },
            a: { component: deepSearchCustomLink },
            h1: { component: DeepSearchH1 },
            h2: { component: DeepSearchH2 },
            h3: { component: DeepSearchH3 },
            ul: { component: DeepSearchUl },
            li: { component: DeepSearchLi },
            hr: { component: DeepSearchHr },
            p: { component: DeepSearchP },
            table: { component: DeepSearchTable },
            thead: { component: DeepSearchThead },
            th: { component: DeepSearchTh },
            td: { component: DeepSearchTd },
            blockquote: { component: DeepSearchBlockquote },
            code: { component: DeepSearchCode },
            pre: { component: DeepSearchPre },
          },
        }}
      >
        {processedContent}
      </Markdown>

      <SourcesSection annotations={annotations} visitedURLs={visitedURLs} />

      <CitationTooltip
        citation={selectedCitation}
        isVisible={showCitationModal}
        onClose={() => {
          setShowCitationModal(false);
          setSelectedCitation(null);
        }}
      />
    </div>
  );
}

// Prop type definitions
const markdownComponentPropTypes = {
  children: PropTypes.node,
};

DeepSearchBotMarkdown.propTypes = {
  content: PropTypes.string.isRequired,
  annotations: PropTypes.array,
  visitedURLs: PropTypes.array,
};

DeepSearchH1.propTypes = markdownComponentPropTypes;
DeepSearchH2.propTypes = markdownComponentPropTypes;
DeepSearchH3.propTypes = markdownComponentPropTypes;
DeepSearchUl.propTypes = markdownComponentPropTypes;
DeepSearchLi.propTypes = markdownComponentPropTypes;
DeepSearchP.propTypes = markdownComponentPropTypes;
DeepSearchTable.propTypes = markdownComponentPropTypes;
DeepSearchThead.propTypes = markdownComponentPropTypes;
DeepSearchTh.propTypes = markdownComponentPropTypes;
DeepSearchTd.propTypes = markdownComponentPropTypes;
DeepSearchBlockquote.propTypes = markdownComponentPropTypes;
DeepSearchCode.propTypes = markdownComponentPropTypes;
DeepSearchPre.propTypes = markdownComponentPropTypes;

export default DeepSearchBotMarkdown;